import { useCallback, useEffect, useRef, useState } from 'react';
import { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';

import { useClientProps } from '../../ProfileMenu/providers/ClientPropsProvider';
import { useProfileMenuV2 } from '../../ProfileMenu/providers/ProfileMenuV2Provider';
import { useIdentityAccountStateInternal } from '../../providers/IdentityAccountStateProvider';
import { RCIDChangeEvent } from '../RCIDChangeEvent';
import { resourceContextStore } from '../ResourceContextStore';

export function useResourceContext() {
  const { resources } = useProfileMenuV2();
  const { onEntitySwitch, onActiveEntityChange, preferredActiveResourceId } = useClientProps();

  // Tracks the last id we notified the host about, so onActiveEntityChange fires
  // once per actual change (not on every unrelated re-render of the effect).
  const lastNotifiedIdRef = useRef<string | undefined>(undefined);

  const { setEntityResourceList, setInitialized } = useIdentityAccountStateInternal();
  const { config } = useClientProps();

  const [activeResourceIdState, setActiveResourceIdState] = useState<string | undefined>(
    resourceContextStore.getActiveResourceId(),
  );

  const [activeResource, setActiveResource] = useState<CoinbaseProfileMenuResource | undefined>();
  const [needsSelection, setNeedsSelection] = useState(false);

  const blockingEntityPickerEnabled = config?.blockingEntityPickerEnabled === true;

  const handleResourceSwitch = useCallback(
    (accountId: string) => {
      resourceContextStore.persistActiveResource(accountId);
      onEntitySwitch?.(accountId);
    },
    [onEntitySwitch],
  );

  const handleBlockingSelection = useCallback(
    async (resourceId: string) => {
      const didPersist = await resourceContextStore.persistActiveResource(resourceId);
      if (!didPersist) return;

      try {
        setNeedsSelection(false);
        const resource = await resourceContextStore.getResourceFromId(resourceId);
        setActiveResource(resource);
        setActiveResourceIdState(resourceId);
        setInitialized(true);
      } catch {
        // getResourceFromId operates on in-memory data — failures here are
        // unexpected but non-fatal. Fall through: the picker remains visible
        // so the user can retry.
        return;
      }

      onEntitySwitch?.(resourceId);
    },
    [setInitialized, onEntitySwitch],
  );

  const initialize = useCallback(async () => {
    const result = await resourceContextStore.initializeAsync();

    if (result.needsSelection && blockingEntityPickerEnabled) {
      setNeedsSelection(true);
      setActiveResourceIdState(undefined);
      setActiveResource(undefined);
      return;
    }

    // If needsSelection but flag is off, fall back to first resource (legacy behavior).
    // Use selectWithoutPersist so the RCID is set in memory (for the middleware)
    // but NOT written to sessionStorage. This way, if the experiment resolves
    // on a later render, initializeAsync() won't find a stored RCID and will
    // correctly return needsSelection: true for the picker.
    if (result.needsSelection && result.resources?.length) {
      // Prefer the host-provided active resource (e.g. the entity whose parent
      // org matches the host's current org) over the first resource, so a host
      // that keeps its own active state isn't silently reset to the first entity.
      const preferred = result.resources.find(
        (resource) => resource.identityAccountId === preferredActiveResourceId,
      );
      await resourceContextStore.selectWithoutPersist(
        (preferred ?? result.resources[0]).identityAccountId,
      );
    }

    const resource = await resourceContextStore.getResourceFromId(
      resourceContextStore.getActiveResourceId(),
    );
    setActiveResource(resource);
    setActiveResourceIdState(resourceContextStore.getActiveResourceId());
    setInitialized(true);
  }, [setInitialized, blockingEntityPickerEnabled, preferredActiveResourceId]);

  useEffect(() => {
    void initialize();
  }, [initialize, resources]);

  // Keep the resolved active resource (drives the chip + ResourceHeader label) in
  // sync with the active id. handleResourceSwitch only persists the id and fires
  // RCID_CHANGE, so without this the chip/header would show a stale resource in
  // no-reload hosts (e.g. Workspace) that switch in-SPA instead of reloading the
  // page the way a full RCID navigation would.
  useEffect(() => {
    if (!activeResourceIdState) {
      return undefined;
    }
    let cancelled = false;
    void resourceContextStore.getResourceFromId(activeResourceIdState).then((resource) => {
      if (cancelled) {
        return;
      }
      setActiveResource(resource);
      // Notify the host once per actual change (init, RCID_CHANGE, or a dropdown
      // switch) so it can reconcile its own active state — not only on click.
      if (lastNotifiedIdRef.current !== activeResourceIdState) {
        lastNotifiedIdRef.current = activeResourceIdState;
        onActiveEntityChange?.(activeResourceIdState);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeResourceIdState, resources, onActiveEntityChange]);

  useEffect(() => {
    const handleRCIDChange = (e: Event) => {
      const event = e as RCIDChangeEvent;
      setActiveResourceIdState(event.detail);
    };

    window.addEventListener('RCID_CHANGE', handleRCIDChange);

    return () => window.removeEventListener('RCID_CHANGE', handleRCIDChange);
  }, []);

  useEffect(() => {
    setEntityResourceList(resources);
  }, [resources, setEntityResourceList]);

  useEffect(() => {
    if (config?.orgSwitcherEnabled === false) {
      setEntityResourceList(undefined);
      setInitialized(false);
    }
  }, [config?.orgSwitcherEnabled, resources, setEntityResourceList, setInitialized]);

  return {
    handleResourceSwitch,
    handleBlockingSelection,
    activeResourceId: activeResourceIdState,
    activeResource,
    resources,
    needsSelection,
  };
}
